import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pencil, Phone, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { DealList } from '../components/DealBits';
import { NewDealForm } from '../components/NewDealForm';
import { BuildingPicker, ContactPicker } from '../components/Pickers';
import { Requirements } from '../components/Requirements';
import { TenureSelect } from '../components/Tenure';
import { Timeline } from '../components/Timeline';
import { Button, Card, Empty, ErrorBox, Field, Input, Modal, PageHeader, Pill, Spinner } from '../components/ui';
import { date, ORG_TYPE_LABEL, personName, phone, relative } from '../lib/format';
import { websiteDomain } from '../lib/import/normalize';
import { supabase, unwrap } from '../lib/supabase';
import { CompanyForm, TIER_COLOR } from './Companies';

function useCompany(id: string) {
  return useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const [org, people, locations, deals, owned] = await Promise.all([
        supabase.from('organizations').select('*, practice_profiles(*), org_aliases(id, alias, alias_type)').eq('id', id).single(),
        supabase.from('contacts').select('id, first_name, last_name, display_name, title, last_contacted_at, contact_emails(email, is_primary)').eq('current_org_id', id).order('last_name'),
        supabase
          .from('org_locations')
          .select('id, tenure, location_type, verification_status, lease_expiration, source_run, buildings(id, name, street, city, state, zip5), suites(suite_number, sf)')
          .eq('org_id', id),
        supabase.from('deals').select('id, name, deal_type, stage, expected_close_date, next_step').eq('org_id', id).order('created_at', { ascending: false }),
        supabase.from('building_ownership').select('id, buildings(id, name, street, city)').eq('owner_org_id', id),
      ]);
      const personIds = unwrap(people).map((p) => p.id);
      const interactions = unwrap(
        await supabase
          .from('interactions')
          .select('id, interaction_type, direction, occurred_at, subject, body_html, body_text, snippet, duration_seconds, source, source_link, thread_id, contact_id, participants, has_attachments, body_storage, mail_account_id, external_id, outcome, reminder_id, contacts(first_name, last_name, display_name)')
          .or(personIds.length ? `org_id.eq.${id},contact_id.in.(${personIds.join(',')})` : `org_id.eq.${id}`)
          .order('occurred_at', { ascending: false })
          .limit(1000),
      );
      return {
        org: unwrap(org),
        people: unwrap(people),
        locations: unwrap(locations),
        deals: unwrap(deals),
        owned: unwrap(owned),
        interactions: interactions.map((i) => ({ ...i, contactName: i.contacts ? personName(i.contacts) : undefined })),
      };
    },
  });
}

export function CompanyDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useCompany(id);
  const [editing, setEditing] = useState(false);
  const [addingPerson, setAddingPerson] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [newDeal, setNewDeal] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['company', id] });

  const addPerson = useMutation({
    mutationFn: async (contactId: string) => unwrap(await supabase.from('contacts').update({ current_org_id: id }).eq('id', contactId)),
    onSuccess: () => {
      setAddingPerson(false);
      refresh();
    },
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorBox error={error ?? 'Company not found'} />;
  const { org } = data;
  const p = Array.isArray(org.practice_profiles) ? org.practice_profiles[0] : org.practice_profiles;

  return (
    <div>
      <PageHeader
        title={org.name}
        subtitle={
          <div className="flex flex-wrap items-center gap-3">
            <span>{ORG_TYPE_LABEL[org.org_type]}</span>
            {p?.tier && <Pill color={TIER_COLOR[p.tier]}>Tier {p.tier}</Pill>}
            {org.website && (
              <a href={org.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
                {websiteDomain(org.website) ?? org.website} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {org.main_phone && (
              <a href={`tel:${org.main_phone}`} className="inline-flex items-center gap-1 hover:underline">
                <Phone className="h-3 w-3" /> {phone(org.main_phone)}
              </a>
            )}
            {org.tags.map((t) => (
              <Pill key={t}>{t}</Pill>
            ))}
          </div>
        }
        actions={
          <>
            <Button onClick={() => setNewDeal(true)}>
              <Plus className="h-4 w-4" /> Deal
            </Button>
            <Button onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <Card title={`People (${data.people.length})`} actions={<Button size="sm" variant="ghost" onClick={() => setAddingPerson(true)}>+ Add</Button>}>
            {data.people.length === 0 ? (
              <Empty>No people linked yet.</Empty>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.people.map((c) => (
                  <li key={c.id}>
                    <Link to={`/contacts/${c.id}`} className="font-medium text-slate-800 hover:text-indigo-700">
                      {personName(c)}
                    </Link>
                    <div className="text-xs text-slate-500">
                      {[c.title, c.contact_emails[0]?.email].filter(Boolean).join(' · ')} · last contact {relative(c.last_contacted_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {p && (
            <Card title="Practice Finder">
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                <Dt label="Segment">{p.segment?.replace(/_/g, ' ')}</Dt>
                <Dt label="Specialties">{p.specialties.join(', ')}</Dt>
                <Dt label="Physicians">{p.num_physicians}</Dt>
                <Dt label="APPs">{p.num_apps}</Dt>
                <Dt label="Locations">{p.total_locations}</Dt>
                <Dt label="Clinical">{p.clinical_location_count}</Dt>
                <Dt label="Part of">{p.part_of}</Dt>
                <Dt label="Ownership">
                  {p.ownership_basis && (
                    <span className="line-clamp-1" title={p.ownership_basis}>
                      {p.ownership_basis}
                    </span>
                  )}
                </Dt>
                <Dt label="Runs">{p.source_runs.join(', ')}</Dt>
                <Dt label="Flags">{p.flags.join(', ')}</Dt>
              </dl>
              {p.not_in_latest_run && <div className="mt-2 rounded bg-orange-50 px-2 py-1 text-xs text-orange-800">Not in the latest Practice Finder run.</div>}
              {org.org_aliases.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
                  Also known as: {org.org_aliases.map((a) => a.alias).join(' · ')}
                </div>
              )}
              {p.pf_notes && <p className="mt-2 whitespace-pre-wrap text-xs text-slate-500">{p.pf_notes}</p>}
            </Card>
          )}

          {org.notes && (
            <Card title="Notes">
              <p className="whitespace-pre-wrap text-sm text-slate-600">{org.notes}</p>
            </Card>
          )}

          {data.owned.length > 0 && (
            <Card title="Owns">
              <ul className="space-y-1 text-sm">
                {data.owned.map((o) =>
                  o.buildings ? (
                    <li key={o.id}>
                      <Link to={`/buildings/${o.buildings.id}`} className="hover:text-indigo-700">
                        {o.buildings.name || o.buildings.street}
                        {o.buildings.city && `, ${o.buildings.city}`}
                      </Link>
                    </li>
                  ) : null,
                )}
              </ul>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card title={`Locations (${data.locations.length})`} actions={<Button size="sm" variant="ghost" onClick={() => setAddingLocation(true)}>+ Add</Button>}>
            {data.locations.length === 0 ? (
              <Empty>No known locations.</Empty>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-500">
                  <tr>
                    <th className="pb-1 font-medium">Address</th>
                    <th className="pb-1 font-medium">Type</th>
                    <th className="pb-1 font-medium">Tenure</th>
                    <th className="pb-1 font-medium">Lease exp.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.locations.map((l) => (
                    <tr key={l.id}>
                      <td className="py-1.5">
                        {l.buildings && (
                          <Link to={`/buildings/${l.buildings.id}`} className="hover:text-indigo-700">
                            {l.buildings.name || l.buildings.street}
                            {l.suites?.suite_number && <span className="text-slate-500"> · {l.suites.suite_number}</span>}
                            <span className="text-slate-500">, {l.buildings.city}</span>
                          </Link>
                        )}
                      </td>
                      <td className="py-1.5 text-slate-600">{l.location_type}</td>
                      <td className="py-1.5">
                        <TenureSelect locationId={l.id} tenure={l.tenure} invalidate={[['company', id]]} />
                      </td>
                      <td className="py-1.5 text-slate-600">{l.lease_expiration ? date(l.lease_expiration) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          {(org.org_type === 'practice_tenant' || p) && (
            <Card title="Requirements">
              <Requirements orgId={id} />
            </Card>
          )}
          <Card title="Deals">
            <DealList deals={data.deals} />
          </Card>
          <Card title="Timeline (everyone at this company)">
            <Timeline items={data.interactions} showContact />
          </Card>
        </div>
      </div>

      <Modal title={`Edit ${org.name}`} open={editing} onClose={() => setEditing(false)}>
        <CompanyForm
          company={org}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="Add a person to this company" open={addingPerson} onClose={() => setAddingPerson(false)}>
        <ContactPicker onPick={(o) => addPerson.mutate(o.id)} />
        <p className="mt-2 text-xs text-slate-500">This makes {org.name} their current company (their old one goes into their history).</p>
        <ErrorBox error={addPerson.error} />
      </Modal>
      <Modal title="Add a location" open={addingLocation} onClose={() => setAddingLocation(false)}>
        <AddLocation
          orgId={id}
          onDone={() => {
            setAddingLocation(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="New deal" open={newDeal} onClose={() => setNewDeal(false)}>
        <NewDealForm orgId={id} defaultName={org.name} />
      </Modal>
    </div>
  );
}

function Dt({ label, children }: { label: string; children: React.ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800">{children}</dd>
    </>
  );
}

function AddLocation({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [building, setBuilding] = useState<{ id: string; label: string } | null>(null);
  const [suite, setSuite] = useState('');
  const [tenure, setTenure] = useState('unknown');
  const save = useMutation({
    mutationFn: async () => {
      let suiteId: string | null = null;
      if (suite.trim()) {
        const existing = unwrap(await supabase.from('suites').select('id').eq('building_id', building!.id).eq('suite_number', suite.trim()).maybeSingle());
        suiteId = existing?.id ?? unwrap(await supabase.from('suites').insert({ building_id: building!.id, suite_number: suite.trim() }).select('id').single()).id;
      }
      unwrap(await supabase.from('org_locations').insert({ org_id: orgId, building_id: building!.id, suite_id: suiteId, tenure, verification_source: 'manual' }));
    },
    onSuccess: onDone,
  });
  return (
    <div className="space-y-3">
      {building ? (
        <div className="rounded-md border border-slate-200 px-3 py-1.5 text-sm">{building.label}</div>
      ) : (
        <>
          <BuildingPicker onPick={(o) => setBuilding(o)} />
          <p className="text-xs text-slate-500">
            Not listed? Create it on the <Link to="/buildings" className="text-indigo-600 hover:underline">Buildings</Link> page first.
          </p>
        </>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Suite">
          <Input value={suite} onChange={(e) => setSuite(e.target.value)} />
        </Field>
        <Field label="Tenure">
          <select className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm" value={tenure} onChange={(e) => setTenure(e.target.value)}>
            <option value="unknown">Unknown</option>
            <option value="owned">Owned</option>
            <option value="leased">Leased</option>
          </select>
        </Field>
      </div>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" disabled={!building} busy={save.isPending} onClick={() => save.mutate()}>
          Add location
        </Button>
      </div>
    </div>
  );
}
